import {
  type LineupPositions,
  postGameByGameIdSubstituteMutation,
} from "@aura-frontend/omnibus-az-client";
import { useMutation } from "@tanstack/react-query";
import {
  Button,
  Dialog,
  DialogFooter,
  DialogHeader,
  DialogScrollableContent,
  DialogTitle,
  Select,
  toast,
} from "@win-reality/react-ui";
import clsx from "clsx";
import { useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import { HorizontalMenu } from "@/components/ui/HorizontalMenu/HorizontalMenu";

import { makeOffensiveSub, setCurrentHitter } from "../../store/slices/game";
import {
  setFocusedEventPayload,
  setTrackingState,
} from "../../store/slices/socket";
import { selectFocusedEvent } from "../../store/slices/socket/selectors";
import { setModalState } from "../../store/slices/view";
import type { RootState } from "../../store/store";
import type { IPlayer } from "../../store/types";
import { mapIPlayerToLineupEntry } from "../../utils/api-helpers";
import { handleErrorNotification } from "../../utils/handleErrorNotification";
import { useFetchSavedLineupEntry } from "../../utils/savedLineupEntry";
import { sortAlphabetic } from "../../utils/sorters";
import { useUpdateGameState } from "../../utils/useUpdateGameState";

export const LineupSwapModal = () => {
  const dispatch = useDispatch();
  const fetchSavedLineupEntry = useFetchSavedLineupEntry();
  const updateGameState = useUpdateGameState();
  const { state } = useSelector((state: RootState) => state.view.modalState);
  const { away, home } = useSelector((state: RootState) => state.game.rosters);
  const sessionUuid = useSelector((state: RootState) => state.session.uuid);
  const inningHalf = useSelector(
    (state: RootState) => state.game.gameState.inning.half,
  );

  const { isViewingPreviousEvent } = useSelector(
    (state: RootState) => state.socket,
  );
  const focusedEvent = useSelector(selectFocusedEvent);

  const offenseTeam = inningHalf === "top" ? away : home;
  const teamSide = inningHalf === "top" ? "away" : "home";
  const currentHitter = offenseTeam.currentHitter;
  const hasCurrentHitter = Boolean(currentHitter?.uuid);

  const [activeTab, setActiveTab] = useState<"lineup" | "sub">("lineup");
  const [selectedHitterIndex, setSelectedHitterIndex] = useState<number | null>(
    null,
  );
  const [replacementPlayerId, setReplacementPlayerId] = useState("");

  const replacementPlayer = offenseTeam.roster.find(
    (p) => p.uuid === replacementPlayerId,
  );

  const { mutate: substituteMutation, isPending: isLoadingSubstitute } =
    useMutation({
      mutationFn: postGameByGameIdSubstituteMutation().mutationFn,
      onError: (error: Error) =>
        handleErrorNotification(
          "An error occurred while trying to substitute the player",
          error,
        ),
    });

  const handleClose = () => {
    dispatch(setModalState({ state: null, data: {} }));
    dispatch(setTrackingState(true));
    setReplacementPlayerId("");
    setSelectedHitterIndex(null);
    setActiveTab("lineup");
  };

  // Filter lineup to batting positions only (positions 1-9, which are numbers)
  const battingLineup = (
    offenseTeam.currentLineup as (IPlayer & {
      position: number | LineupPositions;
    })[]
  ).filter((p) => typeof p.position === "number");

  const handleSelectHitter = (index: number) => {
    setSelectedHitterIndex(index);
  };

  const handleConfirmHitter = () => {
    if (selectedHitterIndex === null) {
      handleClose();
      return;
    }
    const player = battingLineup[selectedHitterIndex];
    if (player) {
      dispatch(
        setCurrentHitter({
          team: teamSide,
          player: { ...player, position: selectedHitterIndex + 1 },
        }),
      );
      if (isViewingPreviousEvent) {
        dispatch(
          setFocusedEventPayload({
            ...focusedEvent,
            focusedPlayers: {
              ...focusedEvent.focusedPlayers,
              focusedHitter: { ...player, position: selectedHitterIndex + 1 },
            },
          }),
        );
      }
    }
    handleClose();
  };

  const handleSubCurrentPlayer = async () => {
    if (!replacementPlayer) return;

    const isInBattingLineup = offenseTeam.currentLineup.some(
      (player: IPlayer & { position?: number | LineupPositions }) =>
        player.uuid === replacementPlayer.uuid &&
        typeof player.position === "number",
    );

    if (isInBattingLineup) {
      toast(
        `${replacementPlayer.firstName} ${replacementPlayer.lastName} is already in the lineup!`,
        { variant: "info" },
      );
      return;
    }

    const positionToUse = currentHitter.position || selectedHitterIndex! + 1;
    const out = await fetchSavedLineupEntry(
      teamSide,
      mapIPlayerToLineupEntry(currentHitter, positionToUse),
    );

    substituteMutation(
      {
        body: {
          in: mapIPlayerToLineupEntry(replacementPlayer, positionToUse),
          out,
          side: teamSide,
        },
        path: { game_id: sessionUuid },
      },
      {
        onSuccess: (data) => {
          const lineupIndexToReplace = offenseTeam.currentLineup.findIndex(
            (player) => player.uuid === currentHitter.uuid,
          );

          dispatch(
            makeOffensiveSub({
              team: teamSide,
              index: lineupIndexToReplace,
              newPlayer: {
                ...replacementPlayer,
                position: currentHitter.position,
              },
            }),
          );
          dispatch(
            setCurrentHitter({
              team: teamSide,
              player: {
                ...replacementPlayer,
                position: currentHitter.position,
              },
            }),
          );
          if (isViewingPreviousEvent) {
            dispatch(
              setFocusedEventPayload({
                ...focusedEvent,
                focusedPlayers: {
                  ...focusedEvent.focusedPlayers,
                  focusedHitter: {
                    ...replacementPlayer,
                    position: currentHitter.position,
                  },
                },
              }),
            );
          }
          toast(
            `${replacementPlayer.firstName} ${replacementPlayer.lastName} subbed in!`,
            { variant: "success" },
          );
          updateGameState(data?.gameState, data?.game, true);
          handleClose();
        },
      },
    );
  };

  // Roster options for sub tab, excluding players already in batting lineup
  const subRosterOptions = useMemo(() => {
    const battingUuids = new Set(battingLineup.map((p) => p.uuid));
    return sortAlphabetic(
      offenseTeam.roster.filter((p) => !battingUuids.has(p.uuid)),
      "lastName",
    ).map((player) => ({
      value: player.uuid,
      label: `${player.lastName}, ${player.firstName}`,
    }));
  }, [offenseTeam.roster, battingLineup]);

  return (
    <Dialog
      open={state === "Lineup Swap"}
      onOpenChange={(open) => !open && handleClose()}
      defaultCancelButton={false}
      size="md"
    >
      {({ showDivider, setScrollContainer }) => (
        <>
          <DialogScrollableContent setScrollContainer={setScrollContainer}>
            <DialogHeader>
              <DialogTitle>Change Hitter</DialogTitle>
            </DialogHeader>

            {hasCurrentHitter && (
              <HorizontalMenu
                items={[
                  {
                    label: "Change Active Hitter",
                    active: activeTab === "lineup",
                    onClick: () => setActiveTab("lineup"),
                  },
                  {
                    label: "Sub out Active Hitter",
                    active: activeTab === "sub",
                    onClick: () => setActiveTab("sub"),
                  },
                ]}
              />
            )}

            {activeTab === "lineup" && (
              <div className="mt-4">
                <p className="mb-2 text-sm font-medium">
                  Select batter to be active
                </p>
                <div className="small-scrollbar max-h-[400px] overflow-y-auto">
                  {battingLineup.map((player, index) => (
                    <button
                      type="button"
                      key={player.uuid}
                      className={clsx(
                        // Size
                        "w-full",
                        // Layout
                        "flex items-center",
                        // Spacing
                        "px-4 py-3",
                        // Border
                        "rounded",
                        // Text
                        "text-sm uppercase",
                        // Effects
                        "transition-colors duration-100",
                        // States
                        "cursor-pointer",
                        selectedHitterIndex === index
                          ? "bg-[hsla(218,100%,52%,0.15)] hover:bg-[hsla(218,100%,52%,0.15)]"
                          : "hover:bg-[hsla(218,100%,52%,0.05)]",
                      )}
                      onClick={() => handleSelectHitter(index)}
                    >
                      <span className="w-8">{index + 1})</span>
                      <span className="w-12 text-center text-xs italic text-gray-500">
                        {player.jerseyNumber ? `#${player.jerseyNumber}` : ""}
                      </span>
                      <span className="pl-2">
                        {player.lastName}, {player.firstName.substring(0, 1)}.
                      </span>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {activeTab === "sub" && (
              <div className="mt-4">
                <p className="mb-4 text-sm font-medium">
                  Sub out{" "}
                  <span className="text-blue">
                    {currentHitter.firstName} {currentHitter.lastName}
                  </span>{" "}
                  for:
                </p>
                <Select
                  value={replacementPlayerId || undefined}
                  onValueChange={(value) =>
                    setReplacementPlayerId(String(value))
                  }
                  placeholder="Select a player..."
                  items={subRosterOptions}
                />
              </div>
            )}
          </DialogScrollableContent>

          <DialogFooter showDivider={showDivider}>
            <div className="flex w-full justify-between">
              <Button
                variant="ghost"
                type="button"
                className="underline"
                onClick={handleClose}
              >
                {activeTab === "lineup" ? "Close" : "Cancel"}
              </Button>
              {activeTab === "lineup" ? (
                <Button
                  variant="secondary-flat-light"
                  onClick={handleConfirmHitter}
                  isDisabled={selectedHitterIndex === null}
                >
                  Change Hitter
                </Button>
              ) : (
                <Button
                  variant="secondary-flat-light"
                  onClick={handleSubCurrentPlayer}
                  isDisabled={!replacementPlayer || isLoadingSubstitute}
                  isLoading={isLoadingSubstitute}
                >
                  Confirm
                </Button>
              )}
            </div>
          </DialogFooter>
        </>
      )}
    </Dialog>
  );
};
